import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { plansAPI, creditsAPI } from '../../services/transactionService';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [detailedBalance, setDetailedBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching transaction data...');
      
      // Try to fetch all data, but handle individual failures
      let transactionsData, balanceData;
      
      try {
        transactionsData = await plansAPI.getMyTransactions();
        console.log('🔍 Transactions API success');
      } catch (error) {
        console.error('🔍 Transactions API failed:', error);
        transactionsData = { data: [] };
      }
      
      try {
        balanceData = await creditsAPI.getBalance();
        console.log('🔍 Balance API success');
      } catch (error) {
        console.error('🔍 Balance API failed:', error);
        balanceData = { credit_balance: 0 };
      }
      
      console.log('🔍 Transactions Data:', transactionsData);
      console.log('🔍 Balance Data:', balanceData);
      
      // Handle different response structures with better validation
      const transactionsList = Array.isArray(transactionsData?.data) ? transactionsData.data : 
                              Array.isArray(transactionsData) ? transactionsData : [];
      
      console.log('🔍 Processed transactionsList:', transactionsList);
      
      setTransactions(transactionsList);
      setFilteredTransactions(transactionsList);
      setDetailedBalance(balanceData);
      
      console.log('🔍 Set transactions:', transactionsList);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const getStatusBadge = (status) => {
    const statusConfig = {
      'completed': { color: 'badge bg-success', text: 'Completed' },
      'pending': { color: 'badge bg-warning', text: 'Pending' },
      'processing': { color: 'badge bg-info', text: 'Processing' },
      'cancelled': { color: 'badge bg-danger', text: 'Cancelled' },
      'failed': { color: 'badge bg-danger', text: 'Failed' }
    };

    const config = statusConfig[status.toLowerCase()] || { color: 'badge bg-secondary', text: status };
    
    return (
      <span className={config.color}>
        {config.text}
      </span>
    );
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      setFilteredTransactions(transactions);
    } else {
      const filtered = transactions.filter(transaction => 
        transaction.status?.toLowerCase() === filter.toLowerCase()
      );
      setFilteredTransactions(filtered);
    }
  };

  const getFilterCount = (status) => {
    if (status === 'all') return transactions.length;
    return transactions.filter(transaction => 
      transaction.status?.toLowerCase() === status.toLowerCase()
    ).length;
  };

  if (loading) {
    return (
      <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading transaction history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      <div className="container">
        <main className="py-5">
          {/* Header */}
          <div className="mb-4">
            <h1 className="display-5 fw-bold text-dark mb-2">
              Transaction History
            </h1>
            <p className="lead text-muted">
              View your subscription history and transaction details
            </p>
          </div>

          {/* Debug Info */}
          <div className="alert alert-info mb-4">
            <strong>Debug Info:</strong><br />
            Transactions Count: {transactions.length}<br />
            Filtered Count: {filteredTransactions.length}<br />
            Balance Data: {detailedBalance.data.credit_balance || 'Not loaded'}
          </div>

            {/* Credit Balance Summary */}
            {detailedBalance && (
              <div className="card shadow mb-4">
                <div className="card-body">
                  <h5 className="card-title mb-3">Credit Balance Overview</h5>
                  <div className="row text-center">
                    <div className="col-md-6">
                      <div className="h3 fw-bold text-primary">
                        {detailedBalance.data?.credit_balance || 0}
                      </div>
                      <div className="text-muted">Current Balance</div>
                      <small className="text-muted">User: {detailedBalance.data?.username}</small>
                    </div>
                    <div className="col-md-6">
                      <div className="h3 fw-bold text-info">
                        {transactions.length}
                      </div>
                      <div className="text-muted">Total Purchases</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Tabs */}
            <div className="card shadow mb-4">
              <div className="card-header">
                <h5 className="mb-0">Filter Transactions</h5>
              </div>
              <div className="card-body">
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn ${activeFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleFilterChange('all')}
                  >
                    All ({getFilterCount('all')})
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeFilter === 'completed' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => handleFilterChange('completed')}
                  >
                    Completed ({getFilterCount('completed')})
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeFilter === 'cancelled' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => handleFilterChange('cancelled')}
                  >
                    Cancelled ({getFilterCount('cancelled')})
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeFilter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => handleFilterChange('pending')}
                  >
                    Pending ({getFilterCount('pending')})
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeFilter === 'failed' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => handleFilterChange('failed')}
                  >
                    Failed ({getFilterCount('failed')})
                  </button>
                </div>
              </div>
            </div>

            {/* Purchase History */}
            <div className="card shadow mb-4">
              <div className="card-header">
                <h5 className="mb-0">Purchase History ({filteredTransactions.length} transactions)</h5>
              </div>
              <div className="card-body p-0">
                {console.log('🔍 Rendering filtered transactions:', filteredTransactions)}
                {!Array.isArray(filteredTransactions) || filteredTransactions.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-receipt fs-1 text-muted mb-3"></i>
                    <h3 className="h5 fw-semibold text-dark mb-2">No purchases yet</h3>
                    <p className="text-muted">
                      You haven't made any purchases yet.
                    </p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {filteredTransactions.map((transaction) => (
                      <div key={transaction.id} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                              <i className="bi bi-credit-card text-primary"></i>
                            </div>
                            <div>
                              <div className="fw-semibold text-dark">
                                {transaction.plan_name}
                              </div>
                              <div className="text-muted small">
                                {formatDate(transaction.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            <div className="text-end">
                              <div className="fw-semibold text-dark">
                                ${transaction.amount}
                              </div>
                              <div className="text-muted small">
                                {transaction.credits_to_add} credits
                              </div>
                            </div>
                            {getStatusBadge(transaction.status)}
                          </div>
                        </div>
                        {transaction.error_message && (
                          <div className="mt-2 text-danger small">
                            Error: {transaction.error_message}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>


           </main>
         </div>
       </div>
     );
  };

export default Transactions;
